<template>
  <div
    :class="{ [$class('open')]: active }"
    class="accordion-item"
  >
    <component :is="headingComponent">
      <button
        :id="ids.control"
        :aria-expanded="active"
        :aria-controls="ids.region"
        class="accordion-item__header"
        type="button"
        @click="$emit('toggle-accordion', index)"
      >
        <span
          v-if="title"
          class="accordion-item__title text-left"
          v-text="title"
        />

        <span class="accordion-item__icon">
          <slot
            v-if="active"
            name="closeIcon"
          >
            <icon-minus />
          </slot>
          <slot
            v-else
            name="icon"
          >
            <icon-plus />
          </slot>
        </span>
      </button>
    </component>

    <component
      :is="transition ? 'transition-height' : 'div'"
      :id="ids.region"
      :name="transitionName"
      role="region"
      :aria-labelledby="ids.control"
    >
      <div
        v-show="active"
        class="accordion-item__content"
      >
        <slot name="content" />
      </div>
    </component>
  </div>
</template>

<script>

/**
 * Vue: Accordion item (accordion-item)
 * -----------------------------------------------------------------------------
 * Accordion item that allows for a title and html to be passed in.
 *
 * @param {Number} [index] - Index of accordion item
 * @param {Boolean} [active] - Whether the accordion item is active.
 * @param {String} [title] - Title of accordion.
 * @param {Boolean} [transition] - Transition on open/close of accordion.
 * @param {String} [transitionName] - Name of transition
 */
import IconMinus from '~icons/directional-navigation/minus.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'

import TransitionHeight from '~async/transition-height/transition-height'
import { convertToHandle } from '~/helpers/convert'

export default {
  name: 'AccordionItem',

  components: {
    IconMinus,
    IconPlus,
    TransitionHeight,
  },

  props: {
    index: {
      type: Number,
      default: 0,
    },
    active: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    transition: {
      type: Boolean,
      default: true,
    },
    transitionName: {
      type: String,
      default: 'height',
    },
    headingLevel: {
      type: Number,
      default: 3,
    },
  },

  emits: {
    'toggle-accordion': (payload) => typeof payload === 'number',
  },

  computed: {
    ids() {
      const id = `${convertToHandle(this.title)}-${this.index}`
      return {
        region: `accordion-item-${id}`,
        control: `accordion-control-${id}`,
      }
    },

    headingComponent() {
      return `h${this.headingLevel}`
    },
  },
}
</script>

<style lang="scss">
@import './accordion-item';
</style>
